import { EnhancedLocationDescriptor, V3Routes } from './../../../../routes/index';
import { action, computed, observable, makeObservable } from 'mobx';
import { RemoteResourceV3, updateRemoteResourceV3 } from '../../../../../commonv3/api/RemoteResourceV3';
import { EventReservationIndexView } from '../../../../types/eventReservations';
import { EventReservationId } from '../../../../../commonv3/types/eventReservations';
import { EventReservationApis } from '../../../../apis/eventReservations';
import { DialogState } from '../../../../../commonv3/states/dialogState';
import { translateErrorMessage } from '../../../../../commonv3/api/protocol';
import { ReservationIndexView } from '../../../../types/reservations';
import { ReservationApis, ReservationSummaryApis } from '../../../../apis/reservations';
import { ReservableType } from '../../../../../commonv3/types/reservable';
import { ReservationId } from '../../../../../commonv3/types/reservations';
import { SessionType } from '../../../../types/sessions';
import { GlobalStatesV3 } from '../../../../../commonv3/states/GlobalStatesV3';
import { SubmitState } from '../../../../../commonv3/states/SubmitState';
import { ReservationSummary } from '../../../../types/reservationSummaries';
import { AllReservationIndexView } from '../../../molecules/reservations/ReservationCardList';
import { CancelFormState } from './CancelFormState';
import { Service } from '../../../../../commonv3/types/service';
import { SurveyKeys, SurveyKeysTrialLessonReservationCancel } from '../../../../../commonv3/types/surveys';
import { routingStore } from '../../../../../common/stores/routing';
import { CancelStep } from '../../../../../commonv3/types/cancel';
import { EventCategory, SheEventClient } from '../../../../../commonv3/events/SheEventClient';
import { MembershipPlanGroupKey } from '../../../../types/membershipPlans';

export class ReservationListState {
  eventReservations: RemoteResourceV3<EventReservationIndexView[]> = RemoteResourceV3.loading();
  reservations: RemoteResourceV3<ReservationIndexView[]> = RemoteResourceV3.loading();
  reservationSummary: RemoteResourceV3<ReservationSummary> = RemoteResourceV3.loading();
  cancelFormState: CancelFormState = new CancelFormState();
  cancelDialog: DialogState = new DialogState(false);
  cancelStep: CancelStep | null = null;
  cancellingReservableType: ReservableType | null = null;
  cancellingReservationId: EventReservationId | ReservationId | null = null;
  processingState: SubmitState = new SubmitState();

  constructor() {
    makeObservable<ReservationListState, 'submitCancel'>(this, {
      eventReservations: observable,
      reservations: observable,
      reservationSummary: observable,
      cancelFormState: observable,
      cancelDialog: observable,
      cancelStep: observable,
      cancellingReservableType: observable,
      cancellingReservationId: observable,
      processingState: observable,
      requestCancel: action.bound,
      allReservationList: computed,
      loading: computed,
      submitting: computed,
      isInitialCoachingVisible: computed,
      reservableCoachingTypes: computed,
      reservedRegularCoachingCounts: computed,
      visibleCoachingTypes: computed,
      confirmCancel: action.bound,
      completeCancelSurvey: action.bound,
      submitCancel: action.bound,
      onRequestClose: action.bound,
      onPrimaryButtonClick: action.bound,
      reservationCancelSurveyKey: computed,
      cancellingReservation: computed,
      requestRescheduleTrialReservation: action.bound,
    });
  }

  async init() {
    await updateRemoteResourceV3(
      this.eventReservations,
      (v) => (this.eventReservations = v),
      () => EventReservationApis.findAll([Service.LIKES])
    );
    await updateRemoteResourceV3(
      this.reservations,
      (v) => (this.reservations = v),
      () => ReservationApis.findAll([Service.LIKES])
    );
    await updateRemoteResourceV3(
      this.reservationSummary,
      (v) => (this.reservationSummary = v),
      () => ReservationSummaryApis.show()
    );
    this.cancellingReservationId = null;
    this.cancellingReservableType = null;
    this.cancelFormState.init();
  }

  isReserved(reservableType: ReservableType, reservableId: number): boolean {
    return !!this.allReservationList.find(
      (r) => r.reservable_type === reservableType && r.reservable.id === reservableId
    );
  }

  findSessionReservationsByType(type: SessionType): ReservationIndexView[] {
    return this.reservations.getOrElse([]).filter((r) => r.reservable.session_type === type);
  }

  findSessionReservationsBy(type: SessionType, membershipPlanGroupKey: MembershipPlanGroupKey): ReservationIndexView[] {
    return this.reservations
      .getOrElse([])
      .filter(
        (r) =>
          r.reservable.session_type === type && r.reservable.membership_plan_group_keys.includes(membershipPlanGroupKey)
      );
  }

  requestCancel(reservation: EventReservationIndexView | ReservationIndexView) {
    this.cancelStep = CancelStep.CONFIRMATION;
    this.cancellingReservableType = reservation.reservable_type;
    this.cancellingReservationId = reservation.id;
    this.cancelDialog.open();
  }

  get allReservationList(): AllReservationIndexView[] {
    return [...this.eventReservations.getOrElse([]), ...this.reservations.getOrElse([])];
  }

  get loading(): boolean {
    return this.reservations.loading || this.eventReservations.loading || this.reservationSummary.loading;
  }

  get submitting(): boolean {
    return this.processingState.submitting;
  }

  get isInitialCoachingVisible() {
    if (this.reservationSummary.isEmpty()) {
      return false;
    }
    return this.reservationSummary.get().visible_any_of_the_initial_coachings;
  }

  get reservableCoachingTypes() {
    if (this.reservationSummary.isEmpty()) {
      return [];
    }
    return this.reservationSummary.get().reservable_coaching_keys;
  }

  get reservedRegularCoachingCounts() {
    if (this.reservationSummary.isEmpty()) {
      return [];
    }
    return this.reservationSummary.get().reserved_regular_coaching_counts;
  }

  get visibleCoachingTypes() {
    if (this.reservationSummary.isEmpty()) {
      return [];
    }
    return this.reservationSummary.get().visible_coaching_keys;
  }

  async confirmCancel() {
    if (this.reservationCancelSurveyKey) {
      this.cancelStep = CancelStep.FORM;
    } else {
      try {
        await this.submitCancel();
        this.cancelStep = CancelStep.COMPLETE;
      } catch (e) {
        alert('キャンセルに失敗しました：' + translateErrorMessage(e));
      } finally {
        this.cancelDialog.close();
      }
    }
  }

  async completeCancelSurvey() {
    this.cancelStep = CancelStep.COMPLETE;
  }

  private async submitCancel() {
    await this.processingState.operate(async () => {
      switch (this.cancellingReservableType) {
        case ReservableType.EVENT:
          const cancellingEvent = this.eventReservations
            .getOrElse([])
            .find((event) => event.id === this.cancellingReservationId);
          if (cancellingEvent === undefined) break;
          SheEventClient.reportKarteEvent(EventCategory.EVENT, 'cancel_event_complete', cancellingEvent.reservable);
        // eslint-disable-next-line no-fallthrough
        case ReservableType.TRIAL_LESSON:
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          await EventReservationApis.cancel(this.cancellingReservationId!, this.cancelFormState.getDirtyValue());
          break;
        case ReservableType.SESSION:
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          await ReservationApis.cancel(this.cancellingReservationId!);
          const cancellingSession = this.reservations
            .getOrElse([])
            .find((session) => session.id === this.cancellingReservationId);
          if (cancellingSession === undefined) break;
          SheEventClient.reportKarteEvent(
            EventCategory.SESSION,
            'cancel_session_complete',
            cancellingSession.reservable
          );
          break;
        default:
          throw new Error('Unsupported reservable type: ' + this.cancellingReservableType);
      }
    });
  }

  // キャンセル完了後、プライマリーボタンを選択しなかったときの処理
  async onRequestClose(): Promise<void> {
    this.init();
    routingStore.push(V3Routes.home());
  }

  async onPrimaryButtonClick(location: EnhancedLocationDescriptor): Promise<void> {
    await this.init();
    routingStore.push(location);
  }

  get reservationCancelSurveyKey(): SurveyKeys | null {
    if (this.cancellingReservableType === ReservableType.TRIAL_LESSON) {
      return SurveyKeysTrialLessonReservationCancel[Service.LIKES];
    }

    return null;
  }

  get cancellingReservation() {
    return this.allReservationList.find(
      (r) => r.id == this.cancellingReservationId && r.reservable_type == this.cancellingReservableType
    );
  }

  async requestRescheduleTrialReservation() {
    // 事前に requestCancel が呼び出されている事を前提にしている
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    GlobalStatesV3.routing.push(V3Routes.trialLessonReschedule(this.cancellingReservationId!));
  }
}
